import React, { useState, useEffect, useCallback, ReactElement, useRef } from 'react';
import * as css from './MaintenanceMenus.module.scss';
import '../../css/themes.scss';
import { IconHelpCircle, IconHelpCircleAlt } from 'src/content/icons';
import MaintenanceMenuPanel from 'src/components/MaintenanceMenus/MaintenanceMenuPanel/containers/MaintenanceMenuPanel';
import { MaintenanceMenuRec, ScheduleOption, MileageOption, OpType, ApptOps, UpdateApptOpsInp, DeteremineDeptInp,
    MaintenanceContract, ascCtaInteractionInput, ascMenuInteractionInput, VehicleRec,
    ascElementConfigurationInput, ApptVeh,
    SpecData} from 'src/models';
import { IconButton, SelectList, LoadingProgress, ScrollBar} from '@framework-controls/core';
import { strings } from 'src/localization';
import { sentryStopTransaction } from '@reynolds/error-boundary';
import { ascCtaInteraction, ascMenuInteraction, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';
import { sendSdEvent, getProviderId, getSdMileage } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface IProps {
    menus: MaintenanceMenuRec[];
    scheduleOptions: ScheduleOption[];
    selectedScheduleOption: string;
    mileageOptions: MileageOption[];
    selectedMileageOption: string;
    defaultScheduleOption: string;
    defaultMileageOption: string;
    checkedMileageMenu: string;
    apptOps: ApptOps[];
    specDefaultDept: string;
    theme: string;
    maintContracts: MaintenanceContract[];
    apptVin: string;
    gmDealerCanEarn: string | undefined;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    locale: string;
    apptVeh: ApptVeh;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    setScheduleOption: (id: string) => void;
    getMenus: (value1, value2) => void;
    setMileageOption: (id: string) => void;
    toggleMenu: (id: string) => void;
    UpdateApptOps: (inputOps: UpdateApptOpsInp[]) => void;
    determineDept: (inp: DeteremineDeptInp) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
}

const MaintenanceMenus = (props: IProps) => {
    const [DisplayHelpMessage, setDisplayHelpMessage] = useState(false);
    const [displayMCMessage, setDisplayMCMessage] = useState(false);
    const [loadingMileageMenus, setLoadingMileageMenus] = useState(false);
    const [loadingScheduleOptions, setLoadingScheduleOptions] = useState(false);
    const noStartingAt = (props.theme === 'lexus' || props.theme === 'lexusMonogram') ? true : false;
    const oneNoStartingAt = useRef(false);
    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y' ||
        props.specData?.jaguarTrackingFlag === 'Y' ||
        props.specData?.landRoverTrackingFlag === 'Y';

    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    const onClick = useCallback((e: any) => {
        (DisplayHelpMessage) ? setDisplayHelpMessage(false) : setDisplayHelpMessage(true);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.WhatAreMaintenanceSchedules,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [DisplayHelpMessage, props.vehicles, props.selectedVehicle]);

    const rebuildOps = useCallback(() => {
        const opsArrayCopy: UpdateApptOpsInp[] = [...props.apptOps];
        const opsArray: UpdateApptOpsInp[] = [];
        opsArrayCopy.forEach((op) => {
            if ((op.opType !== OpType.ConditionalMaintMenu) && (op.opType !== OpType.MaintenanceMenu)) {
                opsArray.push(op);
            }
        });
        props.UpdateApptOps(opsArray);
        const defaultDept = props.specDefaultDept;
        const determineDept = {
            defaultDept: defaultDept,
        };
        props.determineDept(determineDept);
    }, [
        props.apptOps,
        props.UpdateApptOps,
        props.determineDept,
        props.specDefaultDept,
    ]);

    const scheduleChangedHandler = (id: string, value: string) => {
        // sentryStartTransaction('GetMenus', 'Schedule Change');
        setLoadingScheduleOptions(true);
        props.getMenus(value, '');
        rebuildOps();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: value,
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'dropdown_menu',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, id, props.vehicles, props.selectedVehicle);
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: value,
            elementSubtype: 'dropdown',
            elementType: 'menu',
            eventAction: 'click',
        };
        ascMenuInteraction(ascMenuInteractionInp);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: value,
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, id);
        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceSelectionStart', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                scheduleServiceVehicleMake: props.apptVeh.make,
                scheduleServiceVehicleMileage: getSdMileage(props.apptVeh.mileage),
                scheduleServiceVehicleModel: props.apptVeh.model,
                scheduleServiceVehicleVin: props.apptVeh.vin,
                scheduleServiceVehicleYear: props.apptVeh.vehYr,
            });
        }
    };

    const mileageChangedHandler = (id: string, value: string) => {
        // sentryStartTransaction('GetMenus', 'Change Mileage');
        setLoadingMileageMenus(true);
        const selectedMileage = props.mileageOptions.find(option => option.value === value);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: selectedMileage?.label ?? '',
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'dropdown_menu',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, id, props.vehicles, props.selectedVehicle);
        const ascMenuInteractionInp : ascMenuInteractionInput = {
            pageType: 'service',
            elementText: selectedMileage?.label ?? '',
            elementSubtype: 'dropdown',
            elementType: 'menu',
            eventAction: 'click',
        };
        ascMenuInteraction(ascMenuInteractionInp);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: selectedMileage?.label ?? '',
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'open',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, id);
        if (props.checkedMileageMenu) {
            props.toggleMenu(props.selectedMileageOption);
        }
        props.getMenus(props.selectedScheduleOption, value);
        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceSelectionStart', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                scheduleServiceVehicleMake: props.apptVeh.make,
                scheduleServiceVehicleMileage: getSdMileage(props.apptVeh.mileage),
                scheduleServiceVehicleModel: props.apptVeh.model,
                scheduleServiceVehicleVin: props.apptVeh.vin,
                scheduleServiceVehicleYear: props.apptVeh.vehYr,
            });
        }
        rebuildOps();
    };

    if ((props.selectedScheduleOption === '') && props.defaultScheduleOption) {
        props.setScheduleOption(props.defaultScheduleOption);
    }

    if ((props.selectedMileageOption === '') && props.defaultMileageOption) {
        props.setMileageOption(props.defaultMileageOption);
    }

    let loadingProgress: ReactElement | null = null;
    if (loadingMileageMenus || loadingScheduleOptions) {
        loadingProgress = <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} />;
    }

    let menusDefinition: ReactElement | null = null;
    if (DisplayHelpMessage) {
        menusDefinition =
            <div>
                <div>
                    <small>
                        {strings.WhatAreMaintenanceSchedulesText1}
                    </small>
                </div>
                <div>
                    <small>
                        {strings.WhatAreMaintenanceSchedulesText2}
                    </small>
                </div>
            </div>;
    }

    let nonVinDisclaimer: ReactElement | null = null;
    const isSpecActive =
        props.specData?.svcSpecs?.maintMenuDiscFlg === 'Y'||
        props.specData?.bdySpecs?.maintMenuDiscFlg === 'Y'||
        props.specData?.ossSpecs?.maintMenuDiscFlg === 'Y';
    if (!props.apptVin && isSpecActive) {
        nonVinDisclaimer = (
            <div className={css.nonVinDisclaimer}>
                {strings.NonVinMaintMenuDisclaimer}
            </div>
        );
    }

    let mileageMenus: ReactElement[] | null = null;
    let mileageOptionFields: ReactElement | null = null;
    if (props.selectedMileageOption) {
        const filteredMileageMenus =
            props.menus.filter(menu => (menu.type === 'mileage' && menu.id === props.selectedMileageOption));
        mileageMenus = filteredMileageMenus.map(panel => <MaintenanceMenuPanel
            id={panel.id}
            key={panel.id}
            theme={props.theme}
        />);
        if (props.mileageOptions.length > 1) {
            mileageOptionFields =
                <span className={css.inputRight} id='maintSelectList'>
                    <p>{strings.MileageOption}</p>
                    <SelectList
                        id='vehicleMileage'
                        value={props.selectedMileageOption}
                        size='full'
                        // classNames={css.inputField}
                        onChange={mileageChangedHandler}
                        dropDownList={props.mileageOptions}
                    />
                </span>;
        }
    }
    let conditionalArea: ReactElement | null = null;
    let conditionalMenus: ReactElement[] | null = null;
    let scheduleOptionFields: ReactElement | null = null;
    if (props.selectedScheduleOption) {
        const filteredConditionalMenus = props.menus.filter(menu => menu.type === 'conditional');
        conditionalMenus = filteredConditionalMenus.map(panel => <MaintenanceMenuPanel
            id={panel.id}
            key={panel.id}
            theme={props.theme}
        />);
        if (conditionalMenus.length > 0) {
            conditionalArea = <span className={css.gridLeft}>{conditionalMenus}</span>;
        }
    }

    if (props.selectedScheduleOption || !props.defaultScheduleOption || (props.scheduleOptions.length > 1)) {
        scheduleOptionFields =
            <span className={css.inputLeft} id='maintSelectList'>
                <p>{strings.ScheduleOption}</p>
                <SelectList
                    id='scheduleOption'
                    value={props.defaultScheduleOption}
                    size='full'
                    // classNames={css.inputField}
                    onChange={scheduleChangedHandler}
                    dropDownList={props.scheduleOptions}
                />
            </span>;
    }

    useEffect(() => {
        if (!props.defaultMileageOption || props.selectedMileageOption) {
            setLoadingMileageMenus(false);
            sentryStopTransaction();
        }
        if (!props.defaultScheduleOption || props.selectedScheduleOption) {
            setLoadingScheduleOptions(false);
            sentryStopTransaction();
        }
    }, [props.defaultMileageOption, props.selectedMileageOption, props.defaultScheduleOption, props.selectedScheduleOption]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Maintenance Menus', '');
    }, [
        props.addLogEntry,
    ]);

    useEffect(() => {
        if (props.maintContracts) {
            props.maintContracts.forEach(contract => {
                if (contract.status === 'Active') {
                    setDisplayMCMessage(true);
                }
            });
        }
    }, [props.maintContracts]);

    let helpDisplay = css.helpDisplay;
    let helpDisplayMini = css.helpDisplayNone;
    if (props.theme === 'mini') {
        helpDisplay = css.helpDisplayNone;
        helpDisplayMini = css.helpDisplay;
    }

    const gmRewardsDisclaimer = props.gmDealerCanEarn === 'Y' ? (
        <div className={css.gmRewardsDisclaimer} id='gmDisclaimer'>
            {strings.GMRewardsDisclaimerServc}
        </div>
    ) : null;

    const questionStyles = classNames(
        css.question,
        {
            [css.questionBMW]: props.theme === 'bmw',
            [css.questionFord]: props.theme === 'ford',

        },
    );

    const questionMiniStyles = classNames(
        css.questionMini,
        {
            [css.questionMiniBMW]: props.theme === 'bmw',
            [css.questionMiniFord]: props.theme === 'ford',

        },
    );

    /* When the theme is any Lexus check the menus to see if any options will display as $XX.XX**
       If so, set to True and display the appropriate disclaimer
    */
    if (noStartingAt) {
        if (props.menus.find((op) => ((op.itemCostHigh === undefined || op.itemCostHigh === '') &&
            !op.itemCostLow?.includes('*')))) {
            oneNoStartingAt.current = true;
        } else {
            oneNoStartingAt.current = false;
        }
    }

    return (
        <ScrollBar scrollVertical>
            <div className={css.body}>
                {displayMCMessage ? <div className={css.maintContractMessage} id='mcContract'>
                    {strings.MaintContractMessage}
                </div> : null}
                <span className={css.gridQuestion}>
                    <div className={helpDisplay}>
                        <IconButton
                            id={'help'}
                            onClick={(e: any) => onClick(e)}
                            variant={'help'}>
                            <div className={questionStyles}>
                                <IconHelpCircle />
                                <div className={questionStyles}>{strings.WhatAreMaintenanceSchedules}</div>
                            </div>
                        </IconButton>
                    </div>
                    <div id='helpMini' className={helpDisplayMini}>
                        <IconButton
                            id={'helpMini'}
                            onClick={(e: any) => onClick(e)}
                            variant={'help'}>
                            <div className={questionStyles}>
                                <IconHelpCircleAlt />
                                <div className={questionMiniStyles}>{strings.WhatAreMaintenanceSchedules}</div>
                            </div>
                        </IconButton>
                    </div>
                </span>
                {menusDefinition}
                { /* might be out of compliance */ }
                {nonVinDisclaimer}
                {gmRewardsDisclaimer}
                <span className={css.gridRow}>
                    {scheduleOptionFields}
                    {mileageOptionFields}
                </span>
                <span>
                    <span className={css.gridRow}>
                        {conditionalArea}
                        <span className={css.gridRight}>
                            {mileageMenus}
                        </span>
                    </span>
                </span>
                {loadingProgress}
            </div>
        </ScrollBar>
    );
};

export default MaintenanceMenus;